import { FileDownload } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- client-side report, no server-side pagination
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchAlarmLiveSummaryReport } from '@sophocles/api/reports';
import { useScope } from '@sophocles/app';
import DataGridToolbar, { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { compareAlphanum } from '@sophocles/utils';

import { exportReportToExcel } from '../utils/reportExport';

const DEFAULT_ALARM_COUNTS = {
  loss_of_power_count: 0,
  bulb_fault_count: 0,
  bad_neutral_count: 0,
  no_current_count: 0,
};

function normalizeRow(row) {
  return {
    id: row.id ?? 0,
    name: row.name ?? '',
    description: row.description ?? '',
    units_with_radios: row.units_with_radios ?? 0,
    units_with_issues: `${row.units_with_issues ?? 0} (${row.percent_with_issues ?? 0}%)`,
    offline_count: row.offline_count ?? 0,
    bulb_count: row.bulb_count ?? 0,
    charting_enabled: row.charting_enabled ? 'YES' : 'NO',
    relay_load_count: row.relay_load_count ?? 0,
    disabled_loads_count: `${row.disabled_loads_count ?? 0} (${row.disabled_loads_percent ?? 0}%)`,
    ...DEFAULT_ALARM_COUNTS,
    ...(row.alarm_counts || {}),
  };
}

function AlarmLiveReportGrid({ singleSiteGroup = false, baseSiteGroupLink }) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();

  const { customer, siteGroup } = useScope();
  const relayLoadTerm = useRelayLoadTerm();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const isBillboard = customer?.is_billboard ?? true;
  const customerId = customer?.id;
  const siteGroupId = siteGroup?.id;

  useEffect(() => {
    if (customerId == null) return;
    setLoading(true);
    fetchAlarmLiveSummaryReport(customerId, singleSiteGroup ? siteGroupId : null)
      .then(data => {
        const next = (data || []).map(normalizeRow).sort((a, b) => compareAlphanum(a.name, b.name));
        setRows(next);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [customerId, singleSiteGroup, siteGroupId]);

  const columns = useMemo(() => {
    const base = [
      { field: 'units_with_radios', headerName: t('reports.numUnits', { defaultValue: '# Units' }), width: 110 },
      {
        field: 'units_with_issues',
        headerName: isBillboard
          ? t('reports.structuresWithIssues', { defaultValue: 'Structures w/ issues' })
          : t('reports.sitesWithIssues', { defaultValue: 'Sites w/ issues' }),
        width: 170,
      },
      { field: 'offline_count', headerName: t('reports.offline', { defaultValue: 'Offline' }), width: 110 },
      {
        field: 'loss_of_power_count',
        headerName: t('reports.lossOfPower', { defaultValue: 'Loss Of Power' }),
        width: 140,
      },
      { field: 'bulb_count', headerName: t('reports.numBulbs', { defaultValue: '# of Bulbs' }), width: 120 },
      { field: 'bulb_fault_count', headerName: t('reports.bulbOutages', { defaultValue: 'Bulb Outages' }), width: 130 },
      {
        field: 'no_current_count',
        headerName: isBillboard
          ? t('reports.noCurrentAlarms', { defaultValue: 'No Current Alarms' })
          : t('reports.zeroPowerAlarms', { defaultValue: 'Zero Power Alarms' }),
        width: 170,
      },
      {
        field: 'bad_neutral_count',
        headerName: t('reports.badNeutralAlarms', { defaultValue: 'Bad Neutral Alarms' }),
        width: 170,
      },
      {
        field: 'charting_enabled',
        headerName: t('reports.chartingIntegration', { defaultValue: 'Charting Integration' }),
        width: 170,
        valueFormatter: value => (value === 'YES' ? t('general.yes') : t('general.no')),
      },
      {
        field: 'relay_load_count',
        headerName: `# ${relayLoadTerm.plural}`,
        width: 130,
      },
      {
        field: 'disabled_loads_count',
        headerName: `# Disabled ${relayLoadTerm.plural}`,
        width: 170,
      },
    ];

    if (singleSiteGroup) return base;

    return [
      {
        field: 'name',
        headerName: t('general.name'),
        flex: 1,
        minWidth: 200,
        renderCell: params =>
          params.row.id ? (
            <Link to={`${baseSiteGroupLink}${params.row.id}/structures`}>{params.value}</Link>
          ) : (
            params.value
          ),
      },
      { field: 'description', headerName: t('general.description'), flex: 1, minWidth: 200 },
      ...base,
    ];
  }, [t, isBillboard, singleSiteGroup, baseSiteGroupLink, relayLoadTerm]);

  const handleExport = useCallback(() => {
    exportReportToExcel({
      title: t('reports.alarmAndMaintenanceLiveSummary'),
      fileName: 'alarm-live-summary',
      rows,
      columns,
      siteGroup: singleSiteGroup ? siteGroup : null,
      isBillboard,
      subHeaderRows: singleSiteGroup
        ? []
        : [
            [
              `${customer?.name ?? ''} - All ${isBillboard ? t('siteGroups.plants', { defaultValue: 'Plants' }) : t('siteGroups.siteGroups', { defaultValue: 'Site Groups' })}`,
            ],
          ],
    });
  }, [t, rows, columns, siteGroup, customer, isBillboard, singleSiteGroup]);

  const exportButton = useMemo(
    () => (
      <ToolbarIconButton
        tooltip={t('general.export', { defaultValue: 'Export' })}
        onClick={handleExport}
        disabled={loading || rows.length === 0}
      >
        {loading ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: 18 }} />}
      </ToolbarIconButton>
    ),
    [t, handleExport, loading, rows.length],
  );

  const toolbarContent = useMemo(
    () => (
      <Typography variant="caption" color="text.secondary">
        {t('reports.dataCurrentAsOf', { defaultValue: 'Data current as of' })} {moment().format('L, LTS')}
      </Typography>
    ),
    [t],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar
        apiRef={apiRef}
        toolbarContent={toolbarContent}
        toolbarActions={exportButton}
        quickFilterPlaceholder={t('general.search')}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: t('general.noResultsMessage') }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Box>
  );
}

export default AlarmLiveReportGrid;
